/*
 * CSV upload history drawer — shows every CSV upload batch the buyer made
 * with status breakdown chips, "Filter to this batch" deep-link, and bulk
 * "Cancel batch" action (cancels still-pending orders only — shipped ones
 * are left alone).
 *
 * Triggered by "CSV history" button next to "Upload CSV" on Daily Orders.
 */
import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import api, { money } from "../../lib/api";
import { toast } from "sonner";
import {
  Filter, FileText, Trash2, RefreshCw, AlertTriangle, X, Calendar, Package, Upload,
} from "lucide-react";

const STATUS_TONES = {
  new:               "bg-amber-50 text-amber-800 border-amber-200",
  pending:           "bg-amber-50 text-amber-800 border-amber-200",
  forwarded:         "bg-indigo-50 text-indigo-700 border-indigo-200",
  packing:           "bg-indigo-50 text-indigo-700 border-indigo-200",
  shipped:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered:         "bg-foreground text-background border-transparent",
  cancelled:         "bg-rose-50 text-rose-700 border-rose-200",
  awaiting_payment:  "bg-rose-50 text-rose-700 border-rose-200",
};

export default function CsvHistoryDrawer({ open, onClose, onFilterBatch, currentBatchFilter, onReupload }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(null);  // batch_id while in flight

  const load = () => {
    setLoading(true);
    api.get("/dropship/orders/csv-batches")
      .then(r => setBatches(r.data || []))
      .catch(() => toast.error("Could not load CSV upload history"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const cancelBatch = async (b) => {
    if (b.cancellable_count === 0) {
      toast.info("No cancellable orders in this batch — they've already shipped or were cancelled.");
      return;
    }
    if (!window.confirm(
      `Cancel ${b.cancellable_count} pending order(s) in this batch?\n\n` +
      `Already-shipped orders are NOT affected. The seller will be notified. ` +
      `This cannot be undone.`
    )) return;
    setCancelling(b.upload_batch_id);
    try {
      const r = await api.post(`/dropship/orders/csv-batches/${b.upload_batch_id}/cancel`);
      toast.success(`Cancelled ${r.data.cancelled} order(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="csv-history-drawer">
        <SheetHeader>
          <SheetTitle>CSV upload history</SheetTitle>
          <SheetDescription>
            Every batch you've uploaded, with status breakdown and bulk-cancel for still-pending orders.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading}
                  className="rounded-full h-8 gap-1.5 text-[12px]"
                  data-testid="csv-history-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/>Refresh
          </Button>
          {currentBatchFilter && (
            <button onClick={() => onFilterBatch(null)}
                    data-testid="clear-batch-filter"
                    className="text-[11.5px] mono uppercase tracking-[0.14em] px-3 py-1.5 rounded-full border border-foreground bg-foreground text-background inline-flex items-center gap-1.5">
              <Filter className="w-3 h-3"/>Filter active · clear
              <X className="w-3 h-3"/>
            </button>
          )}
        </div>

        {loading && batches.length === 0 ? (
          <div className="mt-6 text-center text-[13px] text-foreground/55 py-12">Loading…</div>
        ) : batches.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-border bg-secondary/40 p-8 text-center">
            <FileText className="w-8 h-8 mx-auto text-foreground/30 mb-2"/>
            <div className="text-[13px] text-foreground/55">No CSV uploads yet.</div>
            <div className="text-[11.5px] text-foreground/40 mt-1">Click "Upload CSV" on Daily Orders to create your first batch.</div>
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="csv-batches-list">
            {batches.map(b => (
              <BatchCard key={b.upload_batch_id}
                         b={b}
                         active={currentBatchFilter === b.upload_batch_id}
                         onFilter={() => onFilterBatch(b.upload_batch_id)}
                         onCancel={() => cancelBatch(b)}
                         onReupload={() => onReupload && onReupload(b)}
                         cancelling={cancelling === b.upload_batch_id}/>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function BatchCard({ b, active, onFilter, onCancel, onReupload, cancelling }) {
  const dt = b.uploaded_at ? new Date(b.uploaded_at) : null;
  const dateStr = dt ? dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
  const timeStr = dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className={`rounded-2xl border p-4 ${active ? "border-foreground bg-foreground/5" : "border-border bg-white"}`}
         data-testid={`csv-batch-${b.upload_batch_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-secondary grid place-items-center shrink-0">
          <FileText className="w-4 h-4 text-foreground/65"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13px] font-medium">{b.seller_name || "Supplier"}</div>
            <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/45">{b.upload_batch_id.slice(0, 8)}</span>
            {active && (
              <span className="text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-foreground text-background">
                Filtering
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[11.5px] text-foreground/55 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3"/>{dateStr} · {timeStr}
            </span>
            <span className="inline-flex items-center gap-1">
              <Package className="w-3 h-3"/>{b.order_count} order{b.order_count !== 1 ? "s" : ""} · {b.customer_count} customer{b.customer_count !== 1 ? "s" : ""} · {b.total_qty} units
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">Total</div>
          <div className="text-[15px] font-medium">{money(b.total_value)}</div>
        </div>
      </div>

      {/* Status breakdown chips */}
      <div className="mt-3 flex gap-1 flex-wrap">
        {Object.entries(b.status_breakdown).sort().map(([k, v]) => (
          <span key={k}
                className={`text-[10px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full border ${STATUS_TONES[k] || "bg-secondary text-foreground/60 border-border"}`}>
            {k.replace("_", " ")} · {v}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm"
                onClick={onFilter}
                data-testid={`filter-batch-${b.upload_batch_id}`}
                className="rounded-full h-8 px-3 text-[11.5px] gap-1">
          <Filter className="w-3 h-3"/>{active ? "Filtering" : "Filter to this batch"}
        </Button>
        {onReupload && (
          <Button variant="outline" size="sm"
                  onClick={onReupload}
                  data-testid={`reupload-batch-${b.upload_batch_id}`}
                  title="Open upload dialog with this supplier pre-selected — use the same `order_id` column to override existing orders without duplicating."
                  className="rounded-full h-8 px-3 text-[11.5px] gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
            <Upload className="w-3 h-3"/>Re-upload
          </Button>
        )}
        <div className="ml-auto">
          {b.cancellable_count > 0 ? (
            <Button variant="outline" size="sm"
                    onClick={onCancel}
                    disabled={cancelling}
                    data-testid={`cancel-batch-${b.upload_batch_id}`}
                    className="rounded-full h-8 px-3 text-[11.5px] gap-1 border-rose-200 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-3 h-3"/>
              {cancelling ? "Cancelling…" : `Cancel ${b.cancellable_count} pending`}
            </Button>
          ) : (
            <span className="text-[11px] text-foreground/40 italic">All shipped or cancelled</span>
          )}
        </div>
      </div>
    </div>
  );
}
